import { assertInteger, assertPaise, sum } from "./integer";

/**
 * The price of one order line from the catalog: item, variant and modifiers.
 * Used for cart quotes on the storefront and for order items on the server
 * (P3-T08). The server result is final; clients only preview.
 *
 * The input types are structural and minimal, so the ordering module maps its
 * catalog rows to them without billing-core knowing the database.
 */

export interface PricingVariant {
	id: string;
	pricePaise: number;
}

export interface PricingModifierOption {
	id: string;
	/** May be negative, for example "no cheese, −₹10". */
	priceDeltaPaise: number;
}

export interface PricingItem {
	/** `items.base_price_paise`. Used only when the item has no variants. */
	basePricePaise: number;
	/** `item_variants` of the item. Empty when it has none. */
	variants: readonly PricingVariant[];
	/** Every option of every modifier group attached to the item. */
	modifierOptions: readonly PricingModifierOption[];
}

export interface LineChoice {
	/** Required when the item has variants, null otherwise. */
	variantId: string | null;
	/** Chosen modifier options. Each at most once. */
	modifierOptionIds: readonly string[];
	/** Whole units, at least 1. */
	quantity: number;
}

export interface LinePrice {
	/** The variant price, or the item's base price when it has no variants. */
	unitPricePaise: number;
	/** Sum of the chosen options' price deltas, for one unit. */
	modifiersTotalPaise: number;
	/** The chosen options with their deltas, in the order chosen. */
	modifiers: PricingModifierOption[];
	quantity: number;
	/** (unit price + modifiers total) × quantity. The line gross of the bill. */
	lineTotalPaise: number;
}

export type PricingErrorCode =
	| "VARIANT_REQUIRED"
	| "VARIANT_NOT_ALLOWED"
	| "UNKNOWN_VARIANT"
	| "UNKNOWN_MODIFIER"
	| "DUPLICATE_MODIFIER"
	| "INVALID_QUANTITY"
	| "NEGATIVE_PRICE";

export class PricingError extends Error {
	readonly code: PricingErrorCode;
	constructor(code: PricingErrorCode, message: string) {
		super(message);
		this.name = "PricingError";
		this.code = code;
	}
}

/**
 * Rules:
 * 1. An item with variants is priced by the chosen variant. `base_price_paise`
 *    is then only the "from" price shown on the menu (data-model.md).
 * 2. Each chosen option adds its delta once per unit.
 * 3. The unit price plus modifiers may not be negative.
 * 4. Line total = (unit price + modifiers total) × quantity.
 *
 * Whether options satisfy their group's min and max, and whether anything is
 * available, is checked by the ordering module, not here.
 */
export function priceLine(item: PricingItem, choice: LineChoice): LinePrice {
	assertInteger(choice.quantity, "quantity");
	if (choice.quantity < 1) {
		throw new PricingError(
			"INVALID_QUANTITY",
			`Quantity must be at least 1, got ${choice.quantity}`,
		);
	}

	let unitPricePaise: number;
	if (item.variants.length > 0) {
		if (choice.variantId === null) {
			throw new PricingError("VARIANT_REQUIRED", "This item needs a variant");
		}
		const variant = item.variants.find((v) => v.id === choice.variantId);
		if (!variant) throw new PricingError("UNKNOWN_VARIANT", `Unknown variant ${choice.variantId}`);
		unitPricePaise = variant.pricePaise;
	} else {
		if (choice.variantId !== null) {
			throw new PricingError("VARIANT_NOT_ALLOWED", "This item has no variants");
		}
		unitPricePaise = item.basePricePaise;
	}
	assertPaise(unitPricePaise, "unit price");

	const seen = new Set<string>();
	const modifiers: PricingModifierOption[] = [];
	for (const id of choice.modifierOptionIds) {
		if (seen.has(id)) throw new PricingError("DUPLICATE_MODIFIER", `Option ${id} chosen twice`);
		seen.add(id);
		const option = item.modifierOptions.find((o) => o.id === id);
		if (!option) throw new PricingError("UNKNOWN_MODIFIER", `Unknown option ${id}`);
		assertInteger(option.priceDeltaPaise, "option price delta");
		modifiers.push({ id: option.id, priceDeltaPaise: option.priceDeltaPaise });
	}
	const modifiersTotalPaise = sum(modifiers.map((m) => m.priceDeltaPaise));

	const unitWithModifiers = unitPricePaise + modifiersTotalPaise;
	if (unitWithModifiers < 0) {
		throw new PricingError("NEGATIVE_PRICE", "The options make the price negative");
	}

	return {
		unitPricePaise,
		modifiersTotalPaise,
		modifiers,
		quantity: choice.quantity,
		lineTotalPaise: lineGross(unitPricePaise, modifiersTotalPaise, choice.quantity),
	};
}

/** Line gross = (unit price + modifiers total) × quantity. Step 1 of the bill. */
export function lineGross(
	unitPricePaise: number,
	modifiersTotalPaise: number,
	quantity: number,
): number {
	const total = (unitPricePaise + modifiersTotalPaise) * quantity;
	assertPaise(total, "line total");
	return total;
}
